import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, SlidersHorizontal, X } from "lucide-react";
import { api } from "../lib/api";
import { SHOP_CATEGORIES, SHOP_SUBCATEGORIES, GEMSTONES } from "../lib/content";
import RecentlyViewed from "../components/RecentlyViewed";
import { useShop } from "../context/ShopContext";
import { FadeUp, PageHero } from "../components/Reveal";
import ProductCard from "../components/ProductCard";

const CATEGORY_COPY = {
  vouchers: { title: "Gift the choice of Celtic Gold", sub: "Celtic Gold vouchers are suitable for birthdays, anniversaries, weddings, Christmas and other special occasions." },
  "lab-grown": { title: "Lab-Grown Diamonds", sub: "Modern Diamonds. Exceptional Craftsmanship." },
  celtic: { title: "Celtic Jewellery", sub: "Jewellery inspired by Celtic heritage, symbolism and the rich history of the Isle of Man." },
  mens: { title: "Men's Jewellery", sub: "Thoughtful jewellery and gifts for him — find a gift for him." },
  jewellery: { title: "Sourced Jewellery", sub: "Rings, pendants, earrings, bangles, chains, charms and cufflinks, hand finished in Peel." },
  all: { title: "All Jewellery & Gifts", sub: "The complete Celtic Gold online catalogue." },
};

const PRICE_BUCKETS = [
  { label: "Under £50", min: 0, max: 50 },
  { label: "£50 – £100", min: 50, max: 100 },
  { label: "£100 – £250", min: 100, max: 250 },
  { label: "£250 – £500", min: 250, max: 500 },
  { label: "£500+", min: 500, max: undefined },
];

const SORTS = [
  ["featured", "Featured"],
  ["newest", "Newest"],
  ["price-asc", "Price: Low to High"],
  ["price-desc", "Price: High to Low"],
  ["name", "Name A–Z"],
];

function FilterPanel({ filters, active, onChange, hideTypes = false, showStone = false }) {
  return (
    <div className="space-y-8">
      {!hideTypes && (
      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Jewellery Type</h3>
        <div className="space-y-2">
          {filters.types.map((t) => (
            <label key={t} className="flex items-center gap-3 text-sm text-stone-mist hover:text-ivory cursor-pointer">
              <input
                type="checkbox"
                checked={active.type === t}
                onChange={() => onChange("type", active.type === t ? "" : t)}
                data-testid={`filter-type-${t}`}
                className="accent-[#C5A059]"
              />
              <span className="capitalize">{t}</span>
            </label>
          ))}
        </div>
      </div>
      )}
      {showStone && (
        <div>
          <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Stone Set</h3>
          <label className="flex items-center gap-3 text-sm text-stone-mist hover:text-ivory cursor-pointer">
            <input
              type="checkbox"
              checked={active.stone_set === "yes"}
              onChange={() => onChange("stone_set", active.stone_set === "yes" ? "" : "yes")}
              data-testid="filter-stone-set"
              className="accent-[#C5A059]"
            />
            Stone set only
          </label>
        </div>
      )}
      {showStone && (
        <div>
          <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Gemstone</h3>
          <div className="space-y-2">
            {GEMSTONES.map((g) => (
              <label key={g} className="flex items-center gap-3 text-sm text-stone-mist hover:text-ivory cursor-pointer">
                <input
                  type="checkbox"
                  checked={active.gemstone === g}
                  onChange={() => onChange("gemstone", active.gemstone === g ? "" : g)}
                  data-testid={`filter-gemstone-${g.replace(/\s+/g, "-").toLowerCase()}`}
                  className="accent-[#C5A059]"
                />
                {g}
              </label>
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Metal</h3>
        <div className="space-y-2">
          {filters.metals.map((m) => (
            <label key={m} className="flex items-center gap-3 text-sm text-stone-mist hover:text-ivory cursor-pointer">
              <input
                type="checkbox"
                checked={active.metal === m}
                onChange={() => onChange("metal", active.metal === m ? "" : m)}
                data-testid={`filter-metal-${m.replace(/\s+/g, "-").toLowerCase()}`}
                className="accent-[#C5A059]"
              />
              {m}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Price</h3>
        <div className="space-y-2">
          {PRICE_BUCKETS.map((b) => (
            <label key={b.label} className="flex items-center gap-3 text-sm text-stone-mist hover:text-ivory cursor-pointer">
              <input
                type="checkbox"
                checked={active.price === b.label}
                onChange={() => onChange("price", active.price === b.label ? "" : b.label)}
                data-testid={`filter-price-${b.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className="accent-[#C5A059]"
              />
              {b.label}
            </label>
          ))}
        </div>
      </div>
      {filters.sizes.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Size</h3>
          <div className="flex flex-wrap gap-2">
            {filters.sizes.map((s) => (
              <button
                key={s}
                onClick={() => onChange("size", active.size === s ? "" : s)}
                data-testid={`filter-size-${s}`}
                className={`px-3 py-1.5 text-xs border transition-colors ${
                  active.size === s ? "border-gold text-gold" : "border-white/10 text-stone-mist hover:border-gold/50"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Availability</h3>
        <label className="flex items-center gap-3 text-sm text-stone-mist hover:text-ivory cursor-pointer">
          <input
            type="checkbox"
            checked={active.availability === "in_stock"}
            onChange={() => onChange("availability", active.availability === "in_stock" ? "" : "in_stock")}
            data-testid="filter-in-stock"
            className="accent-[#C5A059]"
          />
          In stock only
        </label>
      </div>
    </div>
  );
}

export default function ProductList({ collectionMode = false }) {
  const { category, slug, sub } = useParams();
  const [searchParams] = useSearchParams();
  const collection = collectionMode ? slug : "";
  const cat = collectionMode ? "" : category || "all";
  const q = searchParams.get("q") || "";
  const subcats = !collectionMode ? SHOP_SUBCATEGORIES[cat] : null;
  const activeSub = subcats?.find((s) => s.slug === sub) || null;
  const lockedTypes = activeSub?.types || "";
  const showStone = !collectionMode && ["jewellery", "mens", "celtic", "lab-grown", "all"].includes(cat);

  const [filters, setFilters] = useState({ metals: [], types: [], sizes: [] });
  const [active, setActive] = useState({ type: "", metal: "", price: "", size: "", availability: "", stone_set: "", gemstone: "" });
  const [sort, setSort] = useState("featured");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [collectionInfo, setCollectionInfo] = useState(null);
  const [visible, setVisible] = useState(24);
  const { wishlist } = useShop();
  const wishlistMode = searchParams.get("wishlist") === "1";
  const wishlistKey = wishlist.map((w) => w.id).join(",");

  useEffect(() => {
    setVisible(24);
  }, [cat, collection, sub, active, sort, q]);

  useEffect(() => {
    api.get("/filters").then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (collectionMode) {
      api.get("/collections").then(({ data }) => setCollectionInfo(data.find((c) => c.slug === collection) || null)).catch(() => {});
    }
  }, [collectionMode, collection]);

  useEffect(() => {
    setLoading(true);
    const bucket = PRICE_BUCKETS.find((b) => b.label === active.price);
    const params = {
      category: cat === "all" ? undefined : cat,
      collection: collection || undefined,
      type: lockedTypes || active.type || undefined,
      metal: active.metal || undefined,
      size: active.size || undefined,
      availability: active.availability || undefined,
      stone_set: active.stone_set || undefined,
      gemstone: active.gemstone || undefined,
      min_price: bucket?.min,
      max_price: bucket?.max,
      q: q || undefined,
      sort,
      limit: 1000,
    };
    api.get("/products", { params }).then(({ data }) => {
      let list = data.items;
      if (wishlistMode) {
        const ids = wishlistKey.split(",").filter(Boolean);
        list = list.filter((p) => ids.includes(p.id));
      }
      setItems(list);
    }).catch(() => setItems([])).finally(() => setLoading(false));
  }, [cat, collection, sub, lockedTypes, active, sort, q, wishlistMode, wishlistKey]);

  const copy = wishlistMode
    ? { title: "Your Wishlist", sub: "Pieces you've saved. Tap the heart again to remove them." }
    : collectionMode
    ? { title: collectionInfo?.name || "Collection", sub: collectionInfo?.description || "" }
    : activeSub
    ? { title: activeSub.name, sub: activeSub.tagline }
    : CATEGORY_COPY[cat] || CATEGORY_COPY.all;

  const onChange = (key, value) => setActive((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => setActive({ type: "", metal: "", price: "", size: "", availability: "", stone_set: "", gemstone: "" });

  useEffect(() => {
    if (!copy.title) return;
    document.title = copy.title === "Shop" ? "Shop Jewellery & Gifts — Celtic Gold, Isle of Man" : `${copy.title} — Celtic Gold`;
    const el = document.querySelector('meta[name="description"]');
    if (el && copy.sub) el.setAttribute("content", copy.sub);
  }, [copy.title, copy.sub]);

  // Category landing with subcategory cards (structured like the Collections page)
  if (subcats && !sub && !q && !wishlistMode) {
    return (
      <main data-testid="product-list">
        <PageHero eyebrow="Shop" title={copy.title} sub={copy.sub} />
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {subcats.map((s, i) => (
              <FadeUp key={s.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/shop/${cat}/${s.slug}`}
                  data-testid={`subcategory-${s.slug}`}
                  className="group relative block h-80 overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700"
                >
                  <img src={s.image} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <h2 className="font-serif text-2xl text-ivory group-hover:text-gold transition-colors duration-500">{s.name}</h2>
                    <p className="text-stone-mist text-sm font-light mt-2 max-w-xs">{s.tagline}</p>
                    <span className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.25em] mt-5">
                      Shop Now <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
            <FadeUp delay={(subcats.length % 3) * 0.08}>
              <Link
                to={`/shop/${cat}/all`}
                data-testid="subcategory-all"
                className="group relative block h-80 overflow-hidden border border-gold/25 hover:border-gold transition-colors duration-700 bg-ink-2/60"
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-7">
                  <h2 className="font-serif text-2xl text-ivory group-hover:text-gold transition-colors duration-500">Shop All {copy.title}</h2>
                  <p className="text-stone-mist text-sm font-light mt-2 max-w-xs">Browse everything in one list, with filters.</p>
                  <span className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.25em] mt-5">
                    View All <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <RecentlyViewed />
        </section>
      </main>
    );
  }

  return (
    <main data-testid="product-list">
      <PageHero eyebrow={collectionMode ? "Collection" : "Shop"} title={q ? `Search: "${q}"` : copy.title} sub={q ? undefined : copy.sub} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex items-center justify-between mb-10 lg:hidden">
          {!wishlistMode && (
            <button
              onClick={() => setMobileFilters(true)}
              data-testid="mobile-filter-toggle"
              className="flex items-center gap-2 border border-gold/40 text-gold px-5 py-3 text-xs uppercase tracking-[0.2em]"
            >
              <SlidersHorizontal className="w-4 h-4" /> Filter & Sort
            </button>
          )}
          <p className="text-stone-mist text-xs font-mono">{items.length} pieces</p>
        </div>

        <div className={`grid grid-cols-1 ${wishlistMode ? "" : "lg:grid-cols-[240px_1fr]"} gap-12`}>
          {!wishlistMode && (
          <aside className="hidden lg:block" data-testid="filter-sidebar">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xs uppercase tracking-[0.25em] text-ivory font-medium">Filters</h2>
              <button onClick={clearFilters} className="text-stone-mist text-xs hover:text-gold transition-colors" data-testid="filter-clear">
                Clear
              </button>
            </div>
            <FilterPanel filters={filters} active={active} onChange={onChange} hideTypes={!!lockedTypes} showStone={showStone} />
          </aside>
          )}

          <div>
            <div className="hidden lg:flex items-center justify-between mb-8">
              <p className="text-stone-mist text-xs font-mono" data-testid="result-count">{items.length} pieces</p>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="sort-select"
                className="bg-ink-2 border border-white/10 text-ivory text-xs uppercase tracking-[0.15em] px-4 py-2.5 outline-none focus:border-gold"
              >
                {SORTS.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-8">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-ink-3 animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-24 border border-white/5" data-testid="no-results">
                <p className="font-serif text-2xl text-ivory mb-3">{wishlistMode ? "Your wishlist is empty" : "Nothing here yet"}</p>
                <p className="text-stone-mist text-sm max-w-md mx-auto">
                  {wishlistMode
                    ? "Tap the heart on any piece to save it here."
                    : collectionMode || cat !== "all"
                    ? "This part of the catalogue is being curated. Call us on 01624 843737 or enquire — a wider selection is always available in our Peel shop."
                    : "Try adjusting your filters or search."}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-8">
                  {items.slice(0, visible).map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                  ))}
                </div>
                {items.length > visible && (
                  <div className="text-center mt-14">
                    <button
                      onClick={() => setVisible((v) => v + 24)}
                      data-testid="load-more"
                      className="border border-gold/40 text-gold px-10 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold hover:text-ink transition-colors duration-300"
                    >
                      Load More ({items.length - visible} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <RecentlyViewed />
      </section>

      <AnimatePresence>
        {mobileFilters && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileFilters(false)} className="fixed inset-0 z-[80] bg-ink/70" />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-y-0 left-0 z-[90] w-80 bg-ink-2 border-r border-gold/20 p-6 overflow-y-auto"
              data-testid="mobile-filter-panel"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xs uppercase tracking-[0.25em] text-ivory">Filter & Sort</h2>
                <button onClick={() => setMobileFilters(false)} data-testid="mobile-filter-close" className="text-stone-mist hover:text-gold">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mb-8">
                <h3 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Sort by</h3>
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="mobile-sort-select" className="w-full bg-ink border border-white/10 text-ivory text-sm px-4 py-3 outline-none">
                  {SORTS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
              <FilterPanel filters={filters} active={active} onChange={onChange} hideTypes={!!lockedTypes} showStone={showStone} />
              <button onClick={() => setMobileFilters(false)} data-testid="mobile-filter-apply" className="w-full mt-10 bg-gold text-ink py-3.5 text-xs uppercase tracking-[0.25em] font-medium">
                Show {items.length} pieces
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </main>
  );
}
